/**
 * ChatPanel — Convex-backed chat threads and messages with @mentions.
 * Sends messages via sendAndReply action which calls Gemini for AI responses.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { ChatContextPoolStrip } from '@/components/ChatContextPoolStrip';
import { Send, Plus, MessageSquare, AtSign, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Suggestion {
  id: string;
  name: string;
  type: 'entity' | 'asset';
  icon?: string;
}

export function ChatPanel() {
  const { currentProjectId, currentSeriesId, setActivityUserId } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const [input, setInput] = useState('');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentSeries = useQuery(
    api.projectSeries.get,
    currentSeriesId && accountId
      ? { id: currentSeriesId as import('../../../convex/_generated/dataModel').Id<'projects'>, accountId }
      : 'skip'
  );
  const threads = useQuery(api.chat.listThreads, {
    accountId,
    projectId: currentSeriesId ?? undefined,
  });
  const messages = useQuery(
    api.chat.listMessages,
    activeThreadId ? { accountId, thread_id: activeThreadId } : 'skip'
  );
  const entities = useQuery(api.entities.list, { accountId });
  const assets = useQuery(api.assets.list, { accountId });
  const createThread = useMutation(api.chat.createThread);
  const sendAndReply = useAction(api.chat.sendAndReply);

  // Build suggestions list from entities and assets
  const allSuggestions = useMemo(() => {
    const entitySuggestions: Suggestion[] = (entities || []).map((e: any) => ({
      id: e._id,
      name: e.displayName || e.name,
      type: 'entity' as const,
      icon: e.entityType === 'venue' ? '🏢' : e.entityType === 'artist' ? '🎤' : '👤',
    }));
    const assetSuggestions: Suggestion[] = (assets || []).map((a: any) => ({
      id: a._id,
      name: a.name,
      type: 'asset' as const,
      icon: '📎',
    }));
    return [...entitySuggestions, ...assetSuggestions];
  }, [entities, assets]);

  // When no threads, create one. When threads load, pick project-linked or first
  useEffect(() => {
    if (!threads) return;
    if (threads.length === 0) {
      createThread({
        accountId,
        title: 'New chat',
        event_id: currentProjectId ?? undefined,
        projectId: currentSeriesId ?? undefined,
      })
        .then((t) => {
          if (t?.id) setActiveThreadId(t.id);
        })
        .catch((err) => {
          console.error('Create thread failed:', err);
          setError('Failed to create chat thread');
        });
      return;
    }
    if (!activeThreadId || !threads.some((t: any) => t.id === activeThreadId)) {
      const forProject = currentProjectId
        ? threads.find((t: any) => t.event_id === currentProjectId)
        : null;
      setActiveThreadId((forProject ?? threads[0]).id);
    }
  }, [threads, currentProjectId, currentSeriesId, createThread, accountId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle @ mentions
  useEffect(() => {
    const lastAtIndex = input.lastIndexOf('@');
    if (lastAtIndex !== -1 && lastAtIndex === input.length - 1) {
      setSuggestions(allSuggestions.slice(0, 10));
      setShowSuggestions(true);
      setSuggestionIndex(0);
    } else if (lastAtIndex !== -1) {
      const query = input.slice(lastAtIndex + 1).toLowerCase();
      if (query.length > 0 && !query.includes(' ')) {
        const filtered = allSuggestions
          .filter((s) => s.name.toLowerCase().includes(query))
          .slice(0, 10);
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
        setSuggestionIndex(0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  }, [input, allSuggestions]);

  const insertMention = (suggestion: Suggestion) => {
    const lastAtIndex = input.lastIndexOf('@');
    const before = input.slice(0, lastAtIndex);
    const after = input.slice(lastAtIndex + 1);
    const queryEnd = after.search(/\s/) === -1 ? after.length : after.search(/\s/);
    const rest = after.slice(queryEnd);
    setInput(before + '@' + suggestion.name + ' ' + rest);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleInsertMention = (name: string) => {
    setInput((prev) => prev + (prev && !prev.endsWith(' ') ? ' ' : '') + `@${name}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      if (showSuggestions) {
        setShowSuggestions(false);
      }
      e.stopPropagation();
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestionIndex((i) => (i + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestionIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[suggestionIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
      }
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !activeThreadId || isSending) return;
    setInput('');
    setError(null);
    setIsSending(true);
    try {
      await sendAndReply({
        accountId,
        thread_id: activeThreadId,
        content: text,
        context: {
          currentProjectId,
          entities: (entities || []).slice(0, 10).map((e: any) => ({
            type: e.entityType,
            name: e.displayName || e.name,
          })),
        },
      });
    } catch (err: any) {
      console.error('Send message failed:', err);
      setError(err?.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleNewChat = async () => {
    try {
      const t = await createThread({
        accountId,
        title: 'New chat',
        event_id: currentProjectId ?? undefined,
        projectId: currentSeriesId ?? undefined,
      });
      if (t?.id) setActiveThreadId(t.id);
    } catch (err) {
      console.error('Create thread failed:', err);
      setError('Failed to create chat thread');
    }
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {currentSeries && (
        <div className="border-b border-border px-3 py-1.5 text-xs font-medium text-primary/80 bg-primary/5 flex items-center gap-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          {currentSeries.name}
        </div>
      )}
      {/* Thread selector */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2 bg-card/50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <select
            value={activeThreadId ?? ''}
            onChange={(e) => setActiveThreadId(e.target.value || null)}
            className="flex-1 min-w-0 rounded border-0 bg-transparent text-sm font-medium focus:ring-0 focus:outline-none pr-6 truncate"
          >
            {threads?.map((t: { id: string; title?: string }) => (
              <option key={t.id} value={t.id}>
                {t.title ?? `Chat ${t.id.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleNewChat}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary flex-shrink-0 transition-colors"
          title="New chat"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <ChatContextPoolStrip onInsertEntity={(_, name) => handleInsertMention(name)} />

      {/* Messages area */}
      <div className="flex-1 overflow-auto p-3 space-y-3">
        {messages === undefined ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in">
            <div className="h-14 w-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
              <MessageSquare className="h-7 w-7 text-muted-foreground/40" />
            </div>
            <p className="text-sm font-medium text-foreground/80">Start a conversation</p>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-[200px]">
              Send a message or use <span className="text-primary font-medium">@</span> to mention entities and assets
            </p>
          </div>
        ) : (
          messages.map((m: { id: string; role: string; content: string; userId?: string }, i: number) => (
            <div
              key={m.id}
              className={cn(
                "animate-fade-in",
                m.role === 'user' ? 'flex justify-end' : 'flex justify-start'
              )}
            >
              <div
                className={cn(
                  "rounded-2xl px-3.5 py-2.5 text-sm max-w-[85%] shadow-sm",
                  m.role === 'user'
                    ? 'bg-primary/20 text-foreground rounded-br-md'
                    : 'bg-card border border-border rounded-bl-md'
                )}
              >
                {m.role !== 'user' && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="h-3 w-3 text-primary" />
                    <span className="text-[10px] font-medium text-primary/80">AI Assistant</span>
                  </div>
                )}
                <MessageContent content={m.content} />
                {m.userId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivityUserId(m.userId!);
                    }}
                    className="mt-1.5 text-[10px] text-muted-foreground hover:text-primary transition-colors"
                  >
                    View activity
                  </button>
                )}
              </div>
            </div>
          ))
        )}
        {isSending && (
          <div className="flex justify-start animate-fade-in">
            <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles className="h-3 w-3 text-primary" />
                <span className="text-[10px] font-medium text-primary/80">AI Assistant</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-primary/60 animate-typing-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 rounded-full bg-primary/60 animate-typing-bounce" style={{ animationDelay: '200ms' }} />
                <span className="h-2 w-2 rounded-full bg-primary/60 animate-typing-bounce" style={{ animationDelay: '400ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error display */}
      {error && (
        <div className="mx-3 mb-2 px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-lg text-xs text-destructive animate-slide-up">
          {error}
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-border p-3 bg-card/30">
        {/* Suggestions dropdown */}
        {showSuggestions && (
          <div className="mb-2 bg-popover border border-border rounded-lg shadow-xl shadow-black/20 max-h-48 overflow-y-auto animate-slide-up">
            {suggestions.map((s, index) => (
              <button
                key={s.id}
                onClick={() => insertMention(s)}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted transition-colors first:rounded-t-lg last:rounded-b-lg",
                  index === suggestionIndex && "bg-primary/10 text-primary"
                )}
              >
                <span className="text-base">{s.icon}</span>
                <span className="font-medium">{s.name}</span>
                <span className="text-[10px] text-muted-foreground ml-auto capitalize px-1.5 py-0.5 rounded bg-muted">{s.type}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message... Use @ to mention"
              className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all placeholder:text-muted-foreground/50"
              rows={2}
              disabled={isSending}
            />
            <button
              onClick={() => setInput((prev) => prev + '@')}
              className="absolute right-2.5 bottom-2.5 p-1 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-all"
              title="Mention entity/asset"
            >
              <AtSign className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={handleSend}
            disabled={!input.trim() || isSending}
            className={cn(
              "rounded-xl p-2.5 transition-all",
              input.trim() && !isSending
                ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/20"
                : "bg-muted text-muted-foreground"
            )}
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// Parse and highlight @mentions in message content
function MessageContent({ content }: { content: string }) {
  const mentionRegex = /@([^\s]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    parts.push(
      <span key={match.index} className="text-primary font-medium bg-primary/10 px-1 rounded">
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return <p className="whitespace-pre-wrap">{parts.length > 0 ? parts : content}</p>;
}
